import React, { FC, useState, useRef, useCallback, useEffect, SyntheticEvent } from 'react';
import { isEmpty, map, get, pull, concat, isUndefined, reduce, join, size, trim } from 'lodash';
import { DropdownType, Dropdown, usePrevious } from '@trip-ux/components';
import { Button } from '@manulife/mux';
import { labelsSelector } from '../../utils';
import { useSelector } from 'react-redux';
import {
  ReduxStore,
  useActions,
  setTravellerJourney,
  searchDepartureLocation,
  searchDestinationLocation,
  editErrors
} from '@trip-ux/redux-store';
import { TRAVEL_LOCATION_LIMIT, EXCLUDE_CAN, CAN } from '@trip-ux/constants';
import { checkTrue, showComponent } from '@trip-ux/common';

const displayOrder = ['iata', 'name', 'city', 'state', 'country'];
const excludeCan = { country: { $regex: EXCLUDE_CAN, $options: 'i' } };
const includeCan = (country: string) => ({ country: { $regex: `^${country}`, $options: 'i' } });
const createPayload = (str: string, clause) => ({
  where: {
    $or: displayOrder.map(key => ({ [key]: { $regex: `^${str}`, $options: 'i' } })),
    ...clause
  },
  groupBy: 'iata',
  limit: TRAVEL_LOCATION_LIMIT
});
const prepareDropList = searchList =>
  map(searchList, list => {
    const str = reduce(displayOrder, (res, key) => (isEmpty(trim(list[key])) ? res : concat(res, trim(list[key]))), []);
    const location = join(str, ', ');
    return { label: location, value: location };
  });

export const checkError = (showPageError, active, val) =>
  (showPageError || (!isUndefined(active) && !active)) && isEmpty(val);

const checkDepartureError = (showpageError, active, departure, labels, valid) => {
  if (active) {
    return false;
  }
  if (typeof active === 'undefined') {
    return false;
  }

  if (checkError(showpageError, active, departure)) {
    //checks if departure City is empty
    return labels.required;
  } else {
    //checks if valid location or no
    return !valid && labels.invalidLoc;
  }
};

const checkDestinationError = (showPageError, active, destination, destinations) =>
  checkError(showPageError, active, destination) && isEmpty(destinations);

const destinationAdd = (destination, setDestination, setShowAddDest, destinations, actions) => {
  if (!isUndefined(destination) && !isEmpty(destination)) {
    if (!destinations.includes(destination)) {
      actions.setTravellerJourney({ destinations: concat(destinations, destination) });
    }
    setDestination('');
    setShowAddDest(false);
  }
};

const removeDestination = (showAddDest, setShowAddDest, destinations, removeDestinationRef) => {
  if (showAddDest === false) {
    setShowAddDest(true);
  }
  if (size(destinations) > 1) {
    setTimeout(
      () =>
        removeDestinationRef.current &&
        removeDestinationRef.current[destinations.length - 1] &&
        removeDestinationRef.current[destinations.length - 1].focus(),
      0
    );
  }
};

const checkTrueCondition = (prev, destinationRef) => {
  !isUndefined(prev) && !checkTrue(prev) && destinationRef.current && destinationRef.current.focus();
};
export const TravelDestination: FC<{
  travelOutsideCanada: boolean;
  country: string;
  showLabels?: boolean;
  isDisabled?: boolean;
  showDepartureDestination?: boolean;
}> = ({ country, travelOutsideCanada, showLabels = true, isDisabled = false, showDepartureDestination = true }) => {
  //NOSONAR
  const { destinations, labels, departureCity, showPageError, travelLocations } = useSelector((store: ReduxStore) => ({
    destinations: get(store.travellerSelection, 'destinations', []),
    departureCity: store.travellerSelection.departure,
    showPageError: store.travellerSelection.showPageError,
    travelLocations: store.travelLocations,
    labels: get(labelsSelector(store), 'traveller_details.travellerDestination')
  }));

  const actions = useActions({ setTravellerJourney, searchDepartureLocation, editErrors, searchDestinationLocation });
  const [showAddDest, setShowAddDest] = useState(true);
  const [destination, setDestination] = useState('');

  const [departureActive, setDepartureActive] = useState<boolean>();
  const [destinationActive, setDestinationActive] = useState<boolean>();

  const destinationRef = useRef<HTMLInputElement>();
  const removeDestinationRef = useRef<HTMLButtonElement[]>([]);

  const [validDepartureLoc, setValidDepartureLoc] = useState(true);

  const onDestAdd = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      destinationAdd(destination, setDestination, setShowAddDest, destinations, actions);
    },
    [destination, setDestination, setShowAddDest, destinations]
  );
  const onRemove = useCallback(
    (e: SyntheticEvent, dest: string) => {
      e.preventDefault();
      pull(destinations, dest);
      actions.setTravellerJourney({ destinations });
    },
    [destinations]
  );
  useEffect(() => {
    removeDestination(showAddDest, setShowAddDest, destinations, removeDestinationRef);
  }, [destinations, removeDestinationRef, showAddDest, setShowAddDest]);
  const prev = usePrevious(showAddDest);
  useEffect(() => {
    checkTrueCondition(prev, destinationRef);
  }, [prev, showAddDest]);

  const departureSearch = useCallback(str => {
    validDepartureLoc && setValidDepartureLoc(false);
    actions.editErrors({ departure: { error: true } });
    size(str) >= 1 && actions.searchDepartureLocation(createPayload(str, includeCan(country)));
  }, []);

  const destinationSearch = useCallback(str => {
    const exclude = travelOutsideCanada ? excludeCan : includeCan(CAN);
    size(str) >= 1 && actions.searchDestinationLocation(createPayload(str, exclude));
  }, []);

  return (
    <div className="travel-destinations grey-light-brd white-bg">
      {showLabels && <p className="h3 mt-4 mb-2">{labels.header}</p>}
      {showComponent(
        showDepartureDestination,
        <Dropdown
          variant={DropdownType.AUTO_SUGGEST}
          name="departureCity"
          placeholder={labels.departureCityPlaceholder}
          onChange={val => {
            actions.setTravellerJourney({ departure: val });
            if (!validDepartureLoc) {
              setValidDepartureLoc(true);
              actions.editErrors({ departure: false });
            }
          }}
          onAutoSuggestChange={departureSearch}
          options={prepareDropList(travelLocations.departure)}
          value={departureCity}
          onFocus={() => {
            setDepartureActive(true);
          }}
          onInputBlur={e => {
            setDepartureActive(false);
            validDepartureLoc && actions.setTravellerJourney({ departure: (e.target as HTMLInputElement).value });
          }}
          isDisabled={isDisabled}
          className="mt-2"
          error={checkDepartureError(showPageError, departureActive, departureCity, labels, validDepartureLoc)}
          id="departureCity">
          {labels.departureCityLabel}
        </Dropdown>
      )}
      {showLabels && <p className="mt-5 mb-2">{labels.travelDestinationNote}</p>}
      {showComponent(
        showDepartureDestination && !isEmpty(destinations),
        <div className="mt-2">
          <legend data-testid="destinationsLegend">{labels.destinationsLegend}</legend>
          {map(destinations, (dest, idx) => (
            <div key={`${dest}-${idx}`} className="d-flex flex-row justify-content-between">
              <p>{dest}</p>
              {size(destinations) > 1 && (
                <Button
                  id={`removeDestination-${idx}`}
                  onClick={e => onRemove(e, dest)}
                  ref={node => (removeDestinationRef.current[idx] = node)}
                  disabled={isDisabled}
                  variant="tertiary"
                  type="button">
                  <div className="mt-1">{labels.removeDestinationLabel}</div>
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {showComponent(
        showDepartureDestination,
        showAddDest && (
          <div className="mt-2">
            <Dropdown
              isDisabled={isDisabled}
              variant={DropdownType.AUTO_SUGGEST}
              name="destinationCity"
              placeholder={labels.travelDestinationPlaceholder}
              onChange={setDestination}
              onAutoSuggestChange={destinationSearch}
              options={prepareDropList(travelLocations.destinations)}
              value={destination}
              id="destinationCity"
              onFocus={() => setDestinationActive(true)}
              onInputBlur={() => setDestinationActive(false)}
              error={
                checkDestinationError(showPageError, destinationActive, destination, destinations) && labels.required
              }
              ref={destinationRef}>
              {isEmpty(destinations) ? labels.travelDestinationLabel : labels.addMultipleDestinationLabel}
            </Dropdown>
            <Button
              id="addDestinationCityButton"
              onFocus={() => setDestinationActive(true)}
              onBlur={() => setDestinationActive(false)}
              variant="primary"
              type="button"
              className="mt-2 w-100"
              disabled={isDisabled}
              onClick={onDestAdd}>
              {labels.addTravelDestinationButton}
            </Button>
          </div>
        )
      )}
    </div>
  );
};

TravelDestination.displayName = 'TravelDestination';
export default TravelDestination;
